/**
 * i18next bootstrap for @garden/web.
 *
 * Per REQ-PRD-§6.1-A (amended 2026-05-26 per ADR-028: trilingual at MVP —
 * RU default, KY first-class, EN first-class) and REQ-PRD-§13.2-A
 * (fallback to RU when a KY *or* EN key is missing). Namespaces: `common`,
 * `auth`, `marketplace` — additional namespaces are added per-feature in
 * later stories (STORY-003-007 added `marketplace`).
 *
 * Language is chosen by:
 *   1. The persisted UI preference (ui-store -> localStorage); see
 *      `resolveInitialLanguage`. This makes a selected language survive a
 *      full page reload — the choice sticks for the session until the
 *      user picks another (e.g. on the login screen).
 *   2. Default 'ru' (REQ-PRD-§3.3-B). Browser-navigator auto-detect is
 *      intentionally NOT applied, so an English-locale browser still
 *      opens in Russian until the user opts into another language.
 *
 * The persisted value is treated as a hint only; once authenticated, the
 * server-side User model's language preference becomes authoritative
 * (STORY-001-001 introduces that field).
 *
 * STORY-001-009 (mechanical consequence of ADR-028 amendments + AC #11):
 *   Added 'en' to SUPPORTED_LANGUAGES + I18N_RESOURCES.en so that the
 *   auth screens can toggle between RU/KY/EN at runtime without falling
 *   back to RU for English. The production language picker UI lands in
 *   STORY-012-012; the wiring here is the minimum needed for AC #11
 *   to pass and for the new locales/en/auth.json to be reachable.
 *
 * Operator-directed addition (ad-hoc, outside the backlog flow — folds
 * into STORY-012-001/-012 when those run): `en/common.json` now exists,
 * so common.* resolves directly in English instead of falling back to
 * RU. Keys intentionally absent from `en/common.json` (e.g.
 * `toastDemoMessage`) still exercise the documented EN -> RU fallback
 * chain (REQ-PRD-§13.2-A).
 */
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import ruCommon from "@/locales/ru/common.json";
import ruAuth from "@/locales/ru/auth.json";
import ruMarketplace from "@/locales/ru/marketplace.json";
import kyCommon from "@/locales/ky/common.json";
import kyAuth from "@/locales/ky/auth.json";
import kyMarketplace from "@/locales/ky/marketplace.json";
import enCommon from "@/locales/en/common.json";
import enAuth from "@/locales/en/auth.json";
import enMarketplace from "@/locales/en/marketplace.json";

// Design-revamp (handoff-2026-06) screen namespaces. These are loaded at
// init (not via per-feature addResourceBundle) so their keys resolve on the
// first paint — a per-feature register called at module scope runs BEFORE
// initI18n() and early-returns, which left the keys raw at runtime.
import ruLanding from "@/locales/ru/landing.json";
import ruOnboarding from "@/locales/ru/onboarding.json";
import ruDashboard from "@/locales/ru/dashboard.json";
import ruMaterials from "@/locales/ru/materials.json";
import ruShops from "@/locales/ru/shops.json";
import ruLibrary from "@/locales/ru/library.json";
import ruSettings from "@/locales/ru/settings.json";
import ruAccount from "@/locales/ru/account.json";
import ruChat from "@/locales/ru/chat.json";
import kyLanding from "@/locales/ky/landing.json";
import kyOnboarding from "@/locales/ky/onboarding.json";
import kyDashboard from "@/locales/ky/dashboard.json";
import kyMaterials from "@/locales/ky/materials.json";
import kyShops from "@/locales/ky/shops.json";
import kyLibrary from "@/locales/ky/library.json";
import kySettings from "@/locales/ky/settings.json";
import kyAccount from "@/locales/ky/account.json";
import kyChat from "@/locales/ky/chat.json";
import enLanding from "@/locales/en/landing.json";
import enOnboarding from "@/locales/en/onboarding.json";
import enDashboard from "@/locales/en/dashboard.json";
import enMaterials from "@/locales/en/materials.json";
import enShops from "@/locales/en/shops.json";
import enLibrary from "@/locales/en/library.json";
import enSettings from "@/locales/en/settings.json";
import enAccount from "@/locales/en/account.json";
import enChat from "@/locales/en/chat.json";

export const SUPPORTED_LANGUAGES = ["ru", "ky", "en"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = "ru";
export const FALLBACK_LANGUAGE: SupportedLanguage = "ru";

/**
 * localStorage key the ui-store mirrors the active language to (and that
 * the LanguageDetector reads). Duplicated as a literal here — rather than
 * imported from ui-store — because ui-store imports THIS module, and a
 * circular import would be fragile. Must stay equal to ui-store's
 * `UI_STORE_LANGUAGE_KEY`.
 */
export const PERSISTED_LANGUAGE_KEY = "garden.ui.language";

/**
 * The language i18next should start in.
 *
 * A previously-selected language (persisted by `ui-store.setLanguage`)
 * wins, so the choice survives a full page load — e.g. the in-memory-JWT
 * logout that bounces the user back to `/login`. New visitors with no
 * saved preference get `DEFAULT_LANGUAGE` ('ru' per REQ-PRD-§3.3-B).
 *
 * Reading localStorage directly here (instead of leaning on the
 * LanguageDetector, which an explicit `lng` would override anyway) keeps
 * the resolved value in lockstep with the ui-store's rehydrated state on
 * first paint — so the language switcher's active button always matches
 * the rendered copy, with no RU→saved flash or desync.
 */
export function resolveInitialLanguage(): SupportedLanguage {
  try {
    const saved = window.localStorage.getItem(PERSISTED_LANGUAGE_KEY);
    if (
      saved !== null &&
      (SUPPORTED_LANGUAGES as readonly string[]).includes(saved)
    ) {
      return saved as SupportedLanguage;
    }
  } catch {
    // localStorage unavailable (private mode / SSR) — fall through.
  }
  return DEFAULT_LANGUAGE;
}

export const I18N_RESOURCES = {
  ru: {
    common: ruCommon,
    auth: ruAuth,
    marketplace: ruMarketplace,
    landing: ruLanding,
    onboarding: ruOnboarding,
    dashboard: ruDashboard,
    materials: ruMaterials,
    shops: ruShops,
    library: ruLibrary,
    settings: ruSettings,
    account: ruAccount,
    chat: ruChat,
  },
  ky: {
    common: kyCommon,
    auth: kyAuth,
    marketplace: kyMarketplace,
    landing: kyLanding,
    onboarding: kyOnboarding,
    dashboard: kyDashboard,
    materials: kyMaterials,
    shops: kyShops,
    library: kyLibrary,
    settings: kySettings,
    account: kyAccount,
    chat: kyChat,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    marketplace: enMarketplace,
    landing: enLanding,
    onboarding: enOnboarding,
    dashboard: enDashboard,
    materials: enMaterials,
    shops: enShops,
    library: enLibrary,
    settings: enSettings,
    account: enAccount,
    chat: enChat,
  },
} as const;

/**
 * Idempotent init. Call from main.tsx; calling twice is a no-op because
 * i18next short-circuits when `isInitialized` is true.
 */
export function initI18n(): typeof i18n {
  if (i18n.isInitialized) {
    return i18n;
  }

  void i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: I18N_RESOURCES,
      // Honour a previously-saved language so the choice persists across
      // reloads; new users fall back to DEFAULT_LANGUAGE ('ru').
      lng: resolveInitialLanguage(),
      fallbackLng: FALLBACK_LANGUAGE,
      supportedLngs: [...SUPPORTED_LANGUAGES],
      ns: [
        "common",
        "auth",
        "marketplace",
        "landing",
        "onboarding",
        "dashboard",
        "materials",
        "shops",
        "library",
        "settings",
        "account",
        "chat",
      ],
      defaultNS: "common",
      // ICU MessageFormat is supported natively by i18next via `{var}`
      // placeholders; the dedicated ICU plugin lands when plurals/gender
      // are first needed (later epics). For now, plain interpolation.
      interpolation: {
        escapeValue: false, // React escapes by default.
      },
      detection: {
        // Hint sources only — authoritative value comes from the server
        // (introduced in STORY-001-001).
        order: ["localStorage", "navigator"],
        // Use the same key the ui-store uses to persist the language.
        lookupLocalStorage: PERSISTED_LANGUAGE_KEY,
        caches: [],
      },
      // Per REQ-PRD-§13.2-A — fire a missing-key warning in dev. Stories
      // can flip `saveMissing: true` + send-to-collector later. For now
      // we let the default console.warn fire so tests can spy on it.
      saveMissing: false,
      missingKeyHandler: (lngs, ns, key, fallbackValue) => {
        // Forward to console.warn — stable hook for the i18n test.
        // Do NOT log raw user input through this path (none here).
        console.warn(
          `[i18n] missing key lng=${lngs.join(",")} ns=${ns} key=${key} fallback=${String(
            fallbackValue,
          )}`,
        );
      },
      returnNull: false,
      react: {
        useSuspense: false,
      },
    });

  /*
   * Keep `<html lang>` in step with the active language.
   *
   * `index.html` ships `lang="ru"` and nothing ever changed it, so a user
   * reading the Kyrgyz or English UI was still served a document declared as
   * Russian. That is not cosmetic: a screen reader picks its voice and
   * pronunciation rules from this attribute, so Kyrgyz was being read aloud
   * with Russian phonetics — and RU/KY/EN are all first-class at MVP
   * (ADR-028). It also drives `:lang()` styling, hyphenation and spell-check.
   *
   * `languageChanged` fires on every switch, and the immediate call covers the
   * initial resolution. Guarded for jsdom/SSR where `document` may be absent.
   */
  const syncDocumentLang = (lng: string): void => {
    if (typeof document === "undefined") return;
    const base = lng.split("-")[0];
    if (base !== undefined && base.length > 0) {
      document.documentElement.lang = base;
    }
  };
  i18n.on("languageChanged", syncDocumentLang);
  syncDocumentLang(i18n.language || resolveInitialLanguage());

  return i18n;
}

export default i18n;
